export type ProjectoStatus = 'Draft' | 'Active' | 'On Hold' | 'Completed' | 'Cancelled';

export type ProjectoItemType =
  | 'Social Media'
  | 'Sponsorship'
  | 'Design'
  | 'Video Production'
  | 'Photography'
  | 'Influencer'
  | 'Venue'
  | 'Equipment'
  | 'Travel'
  | 'PR'
  | 'Other';

export type ProjectoItemPaymentStatus = 'Pending' | 'Invoiced' | 'Paid' | 'Cancelled';

export type ProjectoEntryKind = 'revenue' | 'cost' | 'passthrough';

export interface ProjectoPassthroughCost {
  id: string;
  label: string;
  amount: number;
  paid: boolean;
}

export type ProjectoInvoiceType = 'Receivable' | 'Payable';

export type ProjectoInvoiceStatus =
  | 'Draft'
  | 'Issued'
  | 'Partially Paid'
  | 'Paid'
  | 'Overdue'
  | 'Cancelled';

export interface ProjectoCampaign {
  id: string;
  name: string;
  client_id: string | null;
  client_name: string;
  brand: string;
  reference: string;
  work_code: string | null;
  status: ProjectoStatus;
  start_date: string | null;
  finish_date: string | null;
  notes: string | null;
  drive_link: string | null;
  budget_enabled: boolean;
  budget_amount: number;
  target_margin_pct: number;
  admin_only: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectoCampaignInsert {
  name: string;
  client_id?: string | null;
  client_name?: string;
  brand?: string;
  reference?: string;
  work_code?: string | null;
  status?: ProjectoStatus;
  start_date?: string | null;
  finish_date?: string | null;
  notes?: string | null;
  drive_link?: string | null;
  budget_enabled?: boolean;
  budget_amount?: number;
  target_margin_pct?: number;
  admin_only?: boolean;
  created_by?: string | null;
}

export interface ProjectoPLItem {
  id: string;
  projecto_id: string;
  item_type: ProjectoItemType;
  entry_kind: ProjectoEntryKind;
  passthrough_breakdown: ProjectoPassthroughCost[];
  is_revenue: boolean;
  name: string;
  description: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  currency: string;
  margin_pct: number;
  cost_commission_pct: number;
  cost_commission_mode: 'percent' | 'flat';
  cost_commission_flat: number;
  cost_billing_mode: 'absorbed' | 'reimbursed';
  scheduled_date: string | null;
  actual_date: string | null;
  vendor_name: string | null;
  vendor_contact: string | null;
  contract_ref: string | null;
  link: string | null;
  observations: string | null;
  payment_status: ProjectoItemPaymentStatus;
  expected_payment_date: string | null;
  actual_payment_date: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectoPLItemInsert {
  projecto_id: string;
  item_type?: ProjectoItemType;
  entry_kind?: ProjectoEntryKind;
  passthrough_breakdown?: ProjectoPassthroughCost[];
  is_revenue?: boolean;
  name?: string;
  description?: string | null;
  quantity?: number;
  unit_price?: number;
  currency?: string;
  margin_pct?: number;
  cost_commission_pct?: number;
  cost_commission_mode?: 'percent' | 'flat';
  cost_commission_flat?: number;
  cost_billing_mode?: 'absorbed' | 'reimbursed';
  scheduled_date?: string | null;
  actual_date?: string | null;
  vendor_name?: string | null;
  vendor_contact?: string | null;
  contract_ref?: string | null;
  link?: string | null;
  observations?: string | null;
  payment_status?: ProjectoItemPaymentStatus;
  expected_payment_date?: string | null;
  actual_payment_date?: string | null;
  display_order?: number;
}

export interface ProjectoInvoice {
  id: string;
  projecto_id: string;
  invoice_type: ProjectoInvoiceType;
  number: string | null;
  counterparty_name: string | null;
  counterparty_email: string | null;
  issue_date: string | null;
  due_date: string | null;
  subtotal: number;
  vat_rate: number;
  total: number;
  currency: string;
  status: ProjectoInvoiceStatus;
  amount_paid: number;
  paid_date: string | null;
  pdf_url: string | null;
  notes: string | null;
  is_overdue: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectoInvoiceInsert {
  projecto_id: string;
  invoice_type: ProjectoInvoiceType;
  number?: string | null;
  counterparty_name?: string | null;
  counterparty_email?: string | null;
  issue_date?: string | null;
  due_date?: string | null;
  subtotal?: number;
  vat_rate?: number;
  total?: number;
  currency?: string;
  status?: ProjectoInvoiceStatus;
  amount_paid?: number;
  paid_date?: string | null;
  pdf_url?: string | null;
  notes?: string | null;
  display_order?: number;
}

export interface ProjectoActivityLog {
  id: string;
  projecto_id: string;
  user_id: string | null;
  user_email: string;
  action: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

export interface ProjectoPLSummary {
  totalRevenue: number;
  totalCosts: number;
  absorbedCosts: number;
  reimbursedCosts: number;
  passthroughSpend: number;
  passthroughCommission: number;
  costCommissionRevenue: number;
  billableToClient: number;
  grossProfit: number;
  grossMarginPct: number;
  netProfit: number;
  netMarginPct: number;
  agencyMargin: number;
  agencyMarginPct: number;
  outstandingReceivables: number;
  outstandingPayables: number;
  netCashPosition: number;
  paidCosts: number;
  pendingCosts: number;
}

export const PROJECTO_STATUS_CONFIG: Record<ProjectoStatus, { label: string; color: string; bg: string }> = {
  Draft:     { label: 'Draft',     color: 'text-gray-600',  bg: 'bg-gray-100' },
  Active:    { label: 'Active',    color: 'text-emerald-700', bg: 'bg-emerald-100' },
  'On Hold': { label: 'On Hold',   color: 'text-amber-700', bg: 'bg-amber-100' },
  Completed: { label: 'Completed', color: 'text-blue-700',  bg: 'bg-blue-100' },
  Cancelled: { label: 'Cancelled', color: 'text-gray-500',  bg: 'bg-gray-100' },
};

export const PROJECTO_ITEM_TYPE_CONFIG: Record<ProjectoItemType, { color: string; bg: string }> = {
  'Social Media':    { color: 'text-sky-700',     bg: 'bg-sky-100' },
  'Sponsorship':     { color: 'text-orange-700',  bg: 'bg-orange-100' },
  'Design':          { color: 'text-pink-700',    bg: 'bg-pink-100' },
  'Video Production':{ color: 'text-violet-700',  bg: 'bg-violet-100' },
  'Photography':     { color: 'text-teal-700',    bg: 'bg-teal-100' },
  'Influencer':      { color: 'text-rose-700',    bg: 'bg-rose-100' },
  'Venue':           { color: 'text-stone-700',   bg: 'bg-stone-100' },
  'Equipment':       { color: 'text-zinc-700',    bg: 'bg-zinc-100' },
  'Travel':          { color: 'text-cyan-700',    bg: 'bg-cyan-100' },
  'PR':              { color: 'text-lime-700',    bg: 'bg-lime-100' },
  'Other':           { color: 'text-gray-600',    bg: 'bg-gray-100' },
};

export const PROJECTO_ITEM_TYPES: ProjectoItemType[] = [
  'Social Media',
  'Sponsorship',
  'Design',
  'Video Production',
  'Photography',
  'Influencer',
  'Venue',
  'Equipment',
  'Travel',
  'PR',
  'Other',
];

export const PROJECTO_INVOICE_STATUS_CONFIG: Record<ProjectoInvoiceStatus, { color: string; bg: string }> = {
  Draft:           { color: 'text-gray-600',    bg: 'bg-gray-100' },
  Issued:          { color: 'text-blue-700',    bg: 'bg-blue-100' },
  'Partially Paid':{ color: 'text-amber-700',   bg: 'bg-amber-100' },
  Paid:            { color: 'text-emerald-700', bg: 'bg-emerald-100' },
  Overdue:         { color: 'text-red-700',     bg: 'bg-red-100' },
  Cancelled:       { color: 'text-gray-500',    bg: 'bg-gray-50' },
};
